/**
 * The machine card's 3D model (`spec-floor-plan-item-cards`, R14, R16, criterion 19).
 *
 * A 96 px square, hidden from assistive technology (the product name beside it carries the
 * information) and never under the pointer. The cube glyph and *3D model* are DOM, under the
 * canvas: they show while the model loads, when there is no model and when it fails, and hide once
 * the model has drawn.
 *
 * The model is the one the plan draws (`item.modelFile`, spec correction 2), loaded through the
 * plan's own model cache (`useGLTF` on `useSecureModelURL`) and drawn as the carry draws it
 * (`carry/PlanCarryCanvas.tsx`): `machineMesh` at full height in the normal 3D look, with no status
 * colour. A perspective camera fits it from a three-quarter view (35° round, 20° up), and it turns
 * about its vertical axis at 18° a second. Frames are drawn only while the page is visible and
 * motion is allowed; otherwise the canvas draws on demand, standing at the three-quarter view.
 *
 * Everything that draws is keyed by the model's address, so a late answer for an old address is
 * never shown. A failed load is caught by this preview's own boundary, which logs no name, ID or
 * address (spec correction 4: `MachineBoundary` would log the BID and name). Unmounting releases the
 * WebGL context through R3F's own dispose.
 */
import {
  Component,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { MathUtils, Sphere } from 'three';
import type { Group, PerspectiveCamera } from 'three';
import { machineMesh, ModelSquare, prefersReducedMotion } from '@repo/ui';
import { Icon } from '@repo/design-system';
import { ink, space, typeRole } from '@repo/design-tokens';
import { useSecureModelURL } from '../secureRemoteFiles';
import { MachineMaterialContext } from '../twinEntities/asset/MachineMaterialContext';
import { PlanViewContext } from './PlanHeightScale';
import { MachineLookLights, MachineLookScope } from './MachineLook';

/** The three-quarter view (R16): round from the front, and up from level. */
const VIEW_ROUND = MathUtils.degToRad(35);
const VIEW_UP = MathUtils.degToRad(20);
/** The turn (R16): 18° a second about the vertical axis. */
const TURN_PER_SECOND = MathUtils.degToRad(18);
/** A frame after a long pause (a hidden tab) turns no further than this. */
const MAX_FRAME_SECONDS = 0.1;
/** The camera's vertical field of view, and the room left around the model's turning sphere. */
const FOV_DEGREES = 30;
const FIT_MARGIN = 1.05;

/** The full-height view: the plan's flattened height never reaches the preview (as the carry). */
const FULL_HEIGHT = { getHeightScale: () => 1, getState: () => 'off' as const };

/** The card's model square (`@repo/ui`'s, the strip's own), never under the pointer. */
const Square = styled(ModelSquare)`
  pointer-events: none;
`;

const Fallback = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${space[1]};
  font-family: ${typeRole.caption2.fontFamily};
  font-size: ${typeRole.caption2.fontSize};
  line-height: ${typeRole.caption2.lineHeight};
  font-weight: ${typeRole.caption2.fontWeight};
  color: ${ink[3]};

  &[hidden] {
    display: none;
  }
`;

/** The cube glyph and *3D model* (cards.css `.model-fallback`). */
const ModelFallback = ({ hidden = false }: { hidden?: boolean }) => (
  <Fallback data-model-fallback="" hidden={hidden}>
    <Icon name="cube" size="md" />
    3D model
  </Fallback>
);

const CanvasLayer = styled.div`
  position: absolute;
  inset: 0;
`;

/** Whether the page is visible (`visibilitychange`). */
const subscribeVisibility = (onChange: () => void) => {
  document.addEventListener('visibilitychange', onChange);
  return () => document.removeEventListener('visibilitychange', onChange);
};
const pageVisible = () => document.visibilityState !== 'hidden';
const usePageVisible = () => useSyncExternalStore(subscribeVisibility, pageVisible, () => true);

/** Whether reduced motion is asked for, followed live (the query's `change`), so a preview already open stops. */
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const subscribeReducedMotion = (onChange: () => void) => {
  let query: MediaQueryList | undefined;
  try {
    query = window.matchMedia?.(REDUCED_MOTION);
  } catch {
    return () => {};
  }
  query?.addEventListener?.('change', onChange);
  return () => query?.removeEventListener?.('change', onChange);
};
const useReducedMotion = () =>
  useSyncExternalStore(subscribeReducedMotion, prefersReducedMotion, () => false);

/** A failed load shows the fallback; nothing about the machine or its address is logged. */
class PreviewBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    // No name, BID or address (spec Access, Logging; correction 4).
    console.warn('[MachineModelPreview] model failed to load');
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

/** The model, fitted to the square from the three-quarter view, turning while `turning`. */
const Model = ({
  url,
  turning,
  onDrawn,
}: {
  url: string;
  turning: boolean;
  onDrawn: () => void;
}) => {
  // Suspends while loading and throws on failure: the preview's `Suspense` and boundary.
  const gltf = useGLTF(url);
  const geometry = useMemo(() => machineMesh(gltf.nodes), [gltf.nodes]);
  if (!geometry) throw new Error('model holds no mesh');
  const material = useContext(MachineMaterialContext);
  const camera = useThree(state => state.camera) as PerspectiveCamera;
  const invalidate = useThree(state => state.invalidate);
  const turn = useRef<Group>(null);

  // The sphere the model turns within, about its own vertical axis through its centre.
  const fit = useMemo(() => {
    if (!geometry.boundingBox) geometry.computeBoundingBox();
    const sphere = geometry.boundingBox?.getBoundingSphere(new Sphere()) ?? new Sphere();
    return { centre: sphere.center, r: Math.max(sphere.radius, 1e-3) };
  }, [geometry]);

  useEffect(() => {
    const { r } = fit;
    const distance = (r / Math.sin(MathUtils.degToRad(FOV_DEGREES) / 2)) * FIT_MARGIN;
    camera.fov = FOV_DEGREES;
    camera.position.set(
      distance * Math.cos(VIEW_UP) * Math.sin(VIEW_ROUND),
      distance * Math.sin(VIEW_UP),
      distance * Math.cos(VIEW_UP) * Math.cos(VIEW_ROUND),
    );
    camera.near = Math.max(distance - r * 2, distance / 100);
    camera.far = distance + r * 2;
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
    invalidate();
    onDrawn();
  }, [camera, fit, invalidate, onDrawn]);

  // Standing still, it stands at the three-quarter view.
  useEffect(() => {
    if (turning || !turn.current) return;
    turn.current.rotation.y = 0;
    invalidate();
  }, [turning, invalidate]);

  useFrame((_, delta) => {
    if (!turning || !turn.current) return;
    turn.current.rotation.y += Math.min(delta, MAX_FRAME_SECONDS) * TURN_PER_SECOND;
  });

  const { centre } = fit;
  return (
    <group ref={turn}>
      <mesh geometry={geometry} material={material} position={[-centre.x, -centre.y, -centre.z]} />
    </group>
  );
};

/** One model address: its own canvas, boundary and drawn state, all dropped when the address changes. */
const Stage = ({ url }: { url: string }) => {
  const [drawn, setDrawn] = useState(false);
  const [lost, setLost] = useState(false);
  const visible = usePageVisible();
  const reducedMotion = useReducedMotion();
  const turning = visible && !reducedMotion;
  const onDrawn = useMemo(() => () => setDrawn(true), []);
  const shown = drawn && !lost;

  return (
    <>
      <ModelFallback hidden={shown} />
      {!lost && (
        <CanvasLayer>
          <PreviewBoundary>
            <Canvas
              flat
              frameloop={turning ? 'always' : 'demand'}
              dpr={[1, 2]}
              gl={{ alpha: true, antialias: true }}
              camera={{ fov: FOV_DEGREES }}
              style={{ pointerEvents: 'none' }}
              onCreated={({ gl }) => {
                gl.setClearColor(0x000000, 0);
                // A lost context draws nothing: the fallback comes back.
                gl.domElement.addEventListener('webglcontextlost', () => setLost(true));
              }}
            >
              <PlanViewContext.Provider value={FULL_HEIGHT}>
                <MachineLookScope>
                  <MachineLookLights />
                  <Suspense fallback={null}>
                    <Model url={url} turning={turning} onDrawn={onDrawn} />
                  </Suspense>
                </MachineLookScope>
              </PlanViewContext.Provider>
            </Canvas>
          </PreviewBoundary>
        </CanvasLayer>
      )}
    </>
  );
};

export const MachineModelPreview = ({ modelFile }: { modelFile: string | null }) => {
  const file = modelFile?.trim() ?? '';
  const url = useSecureModelURL(file);
  return (
    <Square aria-hidden="true" data-machine-model-preview="">
      {file === '' || url === '' ? <ModelFallback /> : <Stage key={url} url={url} />}
    </Square>
  );
};
